import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useErrorLog } from '../contexts/ErrorLogContext';
import { API_URL, apiFetch } from '../config/api';
import { getCachedOpsConfig } from '../config/opsConfig';

// Helper function to parse connection errors
function parseConnectionError(errorMsg) {
  if (!errorMsg) return 'Unknown error';
  const msg = errorMsg.toLowerCase();

  if (msg.includes('semaphore timeout') || msg.includes('oserror(22') || msg.includes('error code 121') ||
    (msg.includes('could not open port') && msg.includes('timeout'))) {
    return 'COM port busy or not responding. Close other apps using the port and try again.';
  }
  if (msg.includes('access is denied') || msg.includes('permission denied')) {
    return 'Access denied to COM port. Port may be in use.';
  }
  if (msg.includes('not found') || msg.includes('does not exist')) {
    return 'COM port not found. Scan ports again.';
  }
  if (msg.includes('timeout')) {
    return 'Connection timeout. Check if drone is powered on.';
  }
  if (msg.includes('could not open port')) {
    return 'Could not open COM port. May be in use by another app.';
  }
  if (msg.includes('connection refused') || msg.includes('no route to host') || msg.includes('network is unreachable') || msg.includes('name or service not known')) {
    return 'Network target not reachable. Check host, port, and the 4G/tunnel link.';
  }
  if (msg.includes('connection string') || msg.includes('invalid')) {
    return 'Check the connection format and try again.';
  }
  return errorMsg;
}

const DEFAULT_BAUD_RATE = 57600;
const DEFAULT_TCP_PORT = 5760;
const DEFAULT_UDP_PORT = 14550;
const BAUD_RATES = [57600, 115200, 921600];

// Direct (lab) links. UDP listens on a port like Mission Planner does: SITL or a
// telemetry forwarder sends to this computer. ("udp:<host>" binds the address
// typed in, so asking for a drone's IP there could never work.)
const DIRECT_MODES = [
  { id: 'serial', label: 'USB / Radio', hint: 'A flight controller on USB, or a telemetry radio. The first port found is filled in.' },
  { id: 'udp', label: 'UDP', hint: 'Listens on this port, like Mission Planner. Point SITL or a telemetry forwarder at this computer.' },
  { id: 'tcp', label: 'TCP', hint: 'Connects to a MAVLink TCP server, e.g. SITL on 127.0.0.1:5760.' },
];

// BERAM flies multirotors. This picks the drone's 3D model on the map; older
// drones may still say fixedwing/helicopter, which the map keeps drawing.
const FRAMES = [
  { value: 'quadcopter', label: 'Quadcopter' },
  { value: 'hexacopter', label: 'Hexacopter' },
];
const withCurrent = (options, current, label = String(current)) => (
  current && !options.some((o) => String(o.value) === String(current)) ? [...options, { value: current, label }] : options
);

const SIM_DEFAULT_START = { id: 'nagpur', label: 'Nagpur (21.1458, 79.0882)', lat: 21.1458, lon: 79.0882 };

// Where a new simulator appears: the delivery depot from Settings, when one is set.
function simStarts() {
  const { depotLat, depotLon } = getCachedOpsConfig().delivery || {};
  const lat = Number(depotLat);
  const lon = Number(depotLon);
  const depot = depotLat != null && depotLon != null && Number.isFinite(lat) && Number.isFinite(lon)
    ? [{ id: 'depot', label: `Delivery depot (${lat.toFixed(4)}, ${lon.toFixed(4)})`, lat, lon }]
    : [];
  return [...depot, SIM_DEFAULT_START, { id: 'custom', label: 'Custom coordinates' }];
}

// How a drone reaches UTMS. Onboard is the production path: the drone's own
// computer dials out over 4G, so there is nothing to connect from here. Sim and
// direct links are for demos and the lab, where this app opens the link itself.
const LINK_KINDS = [
  { id: 'onboard', icon: 'cell_tower', title: '4G onboard computer', body: 'The Raspberry Pi on the drone connects to UTMS over 4G by itself. Use this for real flights.', tag: 'Real flights' },
  { id: 'sim', icon: 'smart_toy', title: 'Simulator', body: 'A virtual drone that flies missions in software, for demos and training.' },
  { id: 'direct', icon: 'usb', title: 'Direct link', body: 'USB, telemetry radio, UDP or SITL, opened by this computer. For the bench and the lab.', unavailable: 'Open the BERAM desktop app to link a drone over USB, radio or UDP. This server has no flight bridge.' },
];

const isOnboard = (drone) => String(drone?.connectionString || '').trim().toLowerCase() === 'mqtt';

// ready: telemetry is flowing. noAutopilot: the onboard computer is online but
// its flight controller is not answering. offline: nothing heard from the drone.
function linkState(drone) {
  if (drone?.status === 'connected') return 'ready';
  if (drone?.onboardOnline) return 'noAutopilot';
  return 'offline';
}

function normalizePortValue(value) {
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed > 65535) {
    return null;
  }
  return parsed;
}

// The connection string for a direct link, or what is still missing.
function directLink({ mode, serialTarget, tcpHost, tcpPort, udpPort }) {
  if (mode === 'serial') {
    const port = String(serialTarget || '').trim();
    return port ? { target: port } : { problem: 'Choose a port, or type one (COM6, /dev/ttyUSB0).' };
  }
  if (mode === 'udp') {
    const port = normalizePortValue(udpPort);
    return port ? { target: `udpin:0.0.0.0:${port}` } : { problem: 'Enter a UDP port between 1 and 65535.' };
  }
  const host = String(tcpHost || '').trim();
  const port = normalizePortValue(tcpPort);
  if (!host) return { problem: 'Enter the TCP host, e.g. 127.0.0.1 for SITL.' };
  if (!port) return { problem: 'Enter a TCP port between 1 and 65535.' };
  return { target: `tcp:${host}:${port}` };
}

function simLink(start, lat, lon) {
  if (start.id !== 'custom') return { target: `mock:${start.id}:${start.lat}:${start.lon}` };
  const la = parseFloat(lat);
  const lo = parseFloat(lon);
  if (!Number.isFinite(la) || !Number.isFinite(lo) || Math.abs(la) > 90 || Math.abs(lo) > 180 || (la === 0 && lo === 0)) {
    return { problem: 'Enter a latitude and longitude, e.g. 21.1458 and 79.0882.' };
  }
  return { target: `mock:custom:${la}:${lo}` };
}

// Status lines carry their tone as a leading mark; the dialog shows it as colour.
function statusTone(text) {
  const t = String(text || '');
  const tone = t.startsWith('❌') ? 'err' : t.startsWith('⚠️') ? 'warn' : t.startsWith('✅') ? 'ok' : '';
  return { tone, text: t.replace(/^(❌|⚠️|✅)\s*/u, '') };
}

/* Add / Edit drone dialogs. Scoped: App.css styles every input, select and button
   (bottom margin, full width, focus glow, hover lift), none of which fits here. */
const DRONE_DIALOG_CSS = `
.adw-overlay{position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(0,0,0,.65);}
.adw{width:600px;max-width:100%;max-height:calc(100vh - 32px);display:flex;flex-direction:column;background:var(--bgSecondary);border:1px solid var(--borderSecondary);border-radius:12px;box-shadow:0 24px 64px rgba(0,0,0,.5);overflow:hidden;font-family:var(--font-body);color:var(--textPrimary);}
.adw.sm{width:440px;}
.adw-head{padding:18px 22px 14px;border-bottom:1px solid var(--borderSecondary);}
.adw-head h3{margin:0;font-family:var(--font-label);font-size:14px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--textPrimary);}
.adw-head p{margin:4px 0 0;font-size:12.5px;color:var(--textMuted);}
.adw-body{flex:1;min-height:0;overflow-y:auto;padding:18px 22px;display:flex;flex-direction:column;gap:16px;}
.adw-foot{display:flex;align-items:center;gap:10px;padding:14px 22px;border-top:1px solid var(--borderSecondary);}
.adw-status{flex:1;min-width:0;font-size:12px;line-height:1.4;color:var(--textMuted);}
.adw-status.ok{color:var(--success);} .adw-status.warn{color:var(--warning);} .adw-status.err{color:var(--danger);}
.adw-kinds{display:grid;gap:8px;}
.adw .adw-kind{display:grid;grid-template-columns:28px minmax(0,1fr) auto;gap:12px;align-items:center;width:100%;margin:0;padding:12px 14px;text-align:left;text-transform:none;letter-spacing:normal;font:inherit;background:var(--bgPrimary);border:1px solid var(--borderSecondary);border-radius:8px;color:var(--textPrimary);cursor:pointer;transition:border-color .15s,background .15s;}
.adw .adw-kind:hover{transform:none;background:color-mix(in srgb, var(--accent) 4%, var(--bgPrimary));border-color:color-mix(in srgb, var(--accent) 45%, var(--borderSecondary));}
.adw .adw-kind[aria-checked=true]{background:color-mix(in srgb, var(--accent) 8%, var(--bgPrimary));border-color:var(--accent);}
.adw .adw-kind:disabled{opacity:.55;cursor:not-allowed;background:var(--bgPrimary);border-color:var(--borderSecondary);}
.adw-kind .ic{font-size:22px;color:var(--textMuted);}
.adw-kind[aria-checked=true] .ic{color:var(--accent);}
.adw-kind b{display:block;font-size:13.5px;font-weight:600;}
.adw-kind small{display:block;margin-top:2px;font-size:12px;line-height:1.4;color:var(--textMuted);}
.adw-tag{font-family:var(--font-label);font-size:9.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;padding:3px 7px;border-radius:4px;border:1px solid var(--borderSecondary);color:var(--textMuted);white-space:nowrap;}
.adw-kind[aria-checked=true] .adw-tag{border-color:color-mix(in srgb, var(--accent) 50%, transparent);color:var(--accent);}
.adw-row{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:12px;}
.adw-row.wide{grid-template-columns:minmax(0,1fr) 150px;}
.adw-field{min-width:0;}
.adw-field > label{display:block;margin-bottom:6px;font-family:var(--font-label);font-size:10px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--textMuted);}
.adw input,.adw select{width:100%;height:36px;margin:0;padding:0 10px;box-sizing:border-box;background:var(--bgPrimary);color:var(--textPrimary);border:1px solid var(--borderSecondary);border-radius:6px;font:inherit;font-size:13px;backdrop-filter:none;outline:none;}
.adw input:focus,.adw select:focus{border-color:var(--accent);background:var(--bgPrimary);box-shadow:0 0 0 2px color-mix(in srgb, var(--accent) 18%, transparent);}
.adw input:disabled,.adw select:disabled{opacity:.6;}
.adw input[type=checkbox]{width:16px;height:16px;padding:0;flex-shrink:0;accent-color:var(--accent);box-shadow:none;}
.adw select{appearance:none;-webkit-appearance:none;padding-right:30px;cursor:pointer;background-image:linear-gradient(45deg,transparent 50%,var(--textMuted) 50%),linear-gradient(135deg,var(--textMuted) 50%,transparent 50%);background-position:calc(100% - 15px) 50%,calc(100% - 10px) 50%;background-size:5px 5px;background-repeat:no-repeat;}
.adw select option{background:var(--bgSecondary);color:var(--textPrimary);}
.adw .mono{font-family:var(--font-label);font-size:12.5px;}
.adw-seg{display:flex;border:1px solid var(--borderSecondary);border-radius:6px;overflow:hidden;background:var(--bgPrimary);}
.adw .adw-seg button{flex:1;width:auto;height:34px;margin:0;padding:0 10px;border:0;border-left:1px solid var(--borderSecondary);border-radius:0;background:transparent;color:var(--textMuted);font-family:var(--font-label);font-size:11px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;cursor:pointer;}
.adw .adw-seg button:first-child{border-left:0;}
.adw .adw-seg button:hover{transform:none;background:color-mix(in srgb, var(--textPrimary) 5%, transparent);color:var(--textPrimary);}
.adw .adw-seg button[aria-pressed=true]{background:var(--accent);color:var(--bgPrimary);}
.adw-port{display:flex;gap:8px;}
.adw-hint{margin:6px 0 0;font-size:12px;line-height:1.45;color:var(--textMuted);}
.adw-hint code{font-family:var(--font-label);font-size:11.5px;color:var(--textSecondary);}
.adw-note{padding:10px 12px;border:1px solid var(--borderSecondary);border-radius:8px;background:var(--bgPrimary);font-size:12.5px;line-height:1.5;color:var(--textSecondary);}
.adw-note p{margin:0;} .adw-note p + p{margin-top:6px;}
.adw-check{display:flex;align-items:flex-start;gap:10px;font-size:12.5px;line-height:1.45;color:var(--textPrimary);cursor:pointer;}
.adw .adw-btn{width:auto;height:38px;margin:0;padding:0 18px;border-radius:6px;font-family:var(--font-label);font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;cursor:pointer;white-space:nowrap;}
.adw .adw-btn:hover{transform:none;}
.adw .adw-btn.ghost{background:transparent;border:1px solid var(--borderSecondary);color:var(--textSecondary);}
.adw .adw-btn.ghost:hover{background:color-mix(in srgb, var(--textPrimary) 5%, transparent);color:var(--textPrimary);}
.adw .adw-btn.sm{height:36px;padding:0 12px;font-size:11px;}
.adw .adw-btn.primary{min-width:150px;background:var(--accent);border:1px solid var(--accent);color:var(--bgPrimary);}
.adw .adw-btn.primary:hover{background:var(--accentHover);border-color:var(--accentHover);}
.adw .adw-btn:disabled,.adw .adw-btn:disabled:hover{opacity:.5;cursor:not-allowed;background:var(--disabledBg);border-color:transparent;color:var(--textMuted);}
.adw .adw-btn.ghost:disabled{background:transparent;border-color:var(--borderSecondary);}
@media (max-width: 560px){.adw-row,.adw-row.wide{grid-template-columns:minmax(0,1fr);} .adw-foot{flex-wrap:wrap;} .adw-status{flex-basis:100%;}}
`;

const SETUP_MESSAGES = {
  offline: 'Waiting for the drone. Power it on with its 4G connection.',
  noAutopilot: 'The onboard computer is online. Waiting for the autopilot: check the USB cable to the flight controller.',
  ready: 'Ready. It is live on the map.',
};

// Second half of adding a 4G drone: put its settings on the onboard computer,
// then watch it come online. Also opened from the card of a drone not yet up.
function OnboardSetup({ drone, onClose }) {
  const { showToast } = useNotification();
  const [env, setEnv] = useState('');
  const [envError, setEnvError] = useState('');
  const state = linkState(drone);
  const reached = state === 'ready' ? 2 : state === 'noAutopilot' ? 1 : 0;

  useEffect(() => {
    let cancelled = false;
    apiFetch(`${API_URL}/drones/${encodeURIComponent(drone.id)}/onboarding`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.success) setEnv(data.env);
        else setEnvError(data.message || 'Could not load the drone settings.');
      })
      .catch(() => { if (!cancelled) setEnvError('Could not reach the server to load the drone settings.'); });
    return () => { cancelled = true; };
  }, [drone.id]);

  const copyEnv = async () => {
    try {
      await navigator.clipboard.writeText(env);
      showToast('Settings copied', 'success');
    } catch {
      showToast('The browser blocked copying. Select the settings and copy them by hand.', 'warning');
    }
  };

  const stepNumber = { width: '28px', height: '28px', borderRadius: '50%', border: '1px solid color-mix(in srgb, var(--accentSecondary) 40%, transparent)', color: 'var(--accentSecondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700 };
  const stepTitle = { margin: '3px 0 8px', color: 'var(--textPrimary)', fontSize: '15px', fontWeight: 600 };
  const hint = { margin: '8px 0 0', color: 'var(--textSecondary)', fontSize: '12px', lineHeight: 1.55 };
  const code = { fontFamily: 'monospace', color: 'var(--textPrimary)' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
      <div role="dialog" aria-modal="true" aria-labelledby="onboard-setup-title" className="custom-scrollbar" style={{
        background: 'var(--surface-container-low, #1b1c1d)',
        padding: 'clamp(20px, 4vw, 32px)',
        border: '1px solid color-mix(in srgb, var(--accentSecondary) 20%, transparent)',
        width: '600px',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: '90vh',
        overflowY: 'auto',
        borderRadius: '16px',
        boxShadow: '0 24px 64px rgba(0, 0, 0, 0.55)',
        boxSizing: 'border-box',
      }}>
        <h3 id="onboard-setup-title" style={{ color: 'var(--accentSecondary)', margin: 0, fontSize: '22px', fontWeight: 800 }}>Set up {drone.name}</h3>
        <p style={{ color: 'var(--textSecondary)', margin: '6px 0 24px', fontSize: '14px', lineHeight: 1.5 }}>
          The drone joins the fleet by itself once its onboard computer has these settings.
        </p>

        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '22px' }}>
          <li style={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr)', gap: '12px' }}>
            <span style={stepNumber}>1</span>
            <div>
              <h4 style={stepTitle}>Copy these settings to the drone</h4>
              {env ? (
                <>
                  <pre style={{ margin: 0, padding: '12px 14px', background: 'var(--bgPrimary)', border: '1px solid var(--borderSecondary)', borderRadius: '10px', color: 'var(--textPrimary)', fontSize: '12px', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all', userSelect: 'all' }}>{env}</pre>
                  <button type="button" onClick={copyEnv} style={{ width: 'auto', margin: '10px 0 0', padding: '9px 14px', background: 'color-mix(in srgb, var(--accentSecondary) 10%, transparent)', color: 'var(--accentSecondary)', border: '1px solid color-mix(in srgb, var(--accentSecondary) 40%, transparent)', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>content_copy</span>
                    Copy settings
                  </button>
                </>
              ) : (
                <div style={{ padding: '12px 14px', borderRadius: '10px', border: `1px solid ${envError ? 'color-mix(in srgb, var(--warning) 35%, transparent)' : 'var(--borderSecondary)'}`, background: envError ? 'color-mix(in srgb, var(--warning) 8%, transparent)' : 'var(--bgPrimary)', color: envError ? 'var(--warning)' : 'var(--textMuted)', fontSize: '12px' }}>
                  {envError || 'Loading settings...'}
                </div>
              )}
              <p style={hint}>
                Save them as <span style={code}>.env</span> next to <span style={code}>script.py</span> on the drone's Raspberry Pi.
                Replace <span style={code}>MQTT_USER</span> and <span style={code}>MQTT_PASSWORD</span> with the broker login from your admin.
              </p>
            </div>
          </li>
          <li style={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr)', gap: '12px' }}>
            <span style={stepNumber}>2</span>
            <div>
              <h4 style={stepTitle}>Power on the drone</h4>
              <p style={{ ...hint, marginTop: 0 }}>
                It connects within seconds of getting a 4G signal. If it was already on, restart its worker with <span style={code}>sudo systemctl restart utms-drone</span>.
              </p>
            </div>
          </li>
        </ol>

        <div role="status" aria-live="polite" style={{ marginTop: '24px', padding: '16px', borderRadius: '12px', background: 'var(--bgPrimary)', border: `1px solid ${state === 'ready' ? 'color-mix(in srgb, var(--accentSecondary) 45%, transparent)' : 'var(--borderSecondary)'}` }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {['Onboard computer', 'Autopilot'].map((label, i) => {
              const done = i < reached;
              const current = i === reached;
              const color = done || (current && i === 0) ? 'var(--accentSecondary)' : current ? 'var(--warning)' : 'var(--textMuted)';
              return (
                <React.Fragment key={label}>
                  {i > 0 && <div style={{ flex: 1, minWidth: '16px', height: '2px', margin: '0 12px', background: done ? 'var(--accentSecondary)' : 'var(--borderSecondary)' }} />}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ position: 'relative', display: 'inline-flex', width: '12px', height: '12px', flexShrink: 0 }}>
                      {current && <span className="stitch-ping" style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: color }} />}
                      <span style={{ position: 'relative', width: '12px', height: '12px', boxSizing: 'border-box', borderRadius: '50%', border: `2px solid ${color}`, background: done ? color : 'transparent' }} />
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: done || current ? 'var(--textPrimary)' : 'var(--textMuted)' }}>{label}</span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          <p style={{ margin: '12px 0 0', fontSize: '13px', lineHeight: 1.5, color: state === 'ready' ? 'var(--accentSecondary)' : 'var(--textSecondary)' }}>
            {SETUP_MESSAGES[state]}
          </p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button type="button" onClick={onClose} style={{
            width: 'auto',
            margin: 0,
            padding: '12px 28px',
            background: state === 'ready' ? 'var(--accentSecondary)' : 'transparent',
            color: state === 'ready' ? 'var(--bgPrimary)' : 'var(--textSecondary)',
            border: state === 'ready' ? 'none' : '2px solid rgba(68, 71, 72, 0.5)',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 800,
            cursor: 'pointer',
          }}>
            {state === 'ready' ? 'Done' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DroneFleet({ drones = [], reorderDrones = null, loadDrones: loadDronesProp, selectedDroneId, onDroneSelect, isMobileLayout = false, isCollapsed = false, onToggleCollapsed = null }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin'; // the server resolves ADMIN_EMAIL to this role
  const { showToast, showConfirm } = useNotification();
  const { logError } = useErrorLog();
  const [draggedDroneId, setDraggedDroneId] = useState(null);
  const [dragOverDroneId, setDragOverDroneId] = useState(null);

  const handleReorder = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const fromIndex = drones.findIndex(d => d.id === sourceId);
    const toIndex = drones.findIndex(d => d.id === targetId);
    if (fromIndex === -1 || toIndex === -1) return;

    const next = [...drones];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);

    if (reorderDrones) {
      reorderDrones(next);
    } else {
      try {
        localStorage.setItem('utms_drone_order', JSON.stringify(next.map(d => d.id)));
      } catch (e) {
        console.warn('Failed to save drone order', e);
      }
      if (loadDronesProp) loadDronesProp();
    }
  };

  const [showAddDrone, setShowAddDrone] = useState(false);
  const [newDrone, setNewDrone] = useState({
    name: '',
    baudRate: DEFAULT_BAUD_RATE,
    vehicleModel: 'quadcopter',
  });
  const [linkKind, setLinkKind] = useState('onboard');
  const [setupTarget, setSetupTarget] = useState(null); // { id, name } of the onboard drone being set up
  const [directMode, setDirectMode] = useState('serial'); // direct links only
  const [serialTarget, setSerialTarget] = useState('');
  const [tcpHost, setTcpHost] = useState('127.0.0.1');
  const [tcpPort, setTcpPort] = useState(DEFAULT_TCP_PORT);
  const [udpPort, setUdpPort] = useState(DEFAULT_UDP_PORT);
  const [availablePorts, setAvailablePorts] = useState([]);
  const [scanningPorts, setScanningPorts] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('');
  const [editingDrone, setEditingDrone] = useState(null);
  const [editDroneData, setEditDroneData] = useState({ name: '', connectionString: '', baudRate: DEFAULT_BAUD_RATE, vehicleModel: 'quadcopter' });
  const [editSaving, setEditSaving] = useState(false);

  const [connectingDrones, setConnectingDrones] = useState({});
  const [simStart, setSimStart] = useState('');
  const [simLat, setSimLat] = useState('');
  const [simLon, setSimLon] = useState('');
  // Direct links are opened by the desktop app itself (it sets window.__APP_VERSION__),
  // or on the web by the server's flight bridge. Production web has no bridge.
  const isDesktopApp = typeof window !== 'undefined' && Boolean(window.__APP_VERSION__);
  const [bridgeUp, setBridgeUp] = useState(null);
  const directAvailable = isDesktopApp || bridgeUp === true;

  const isDirect = linkKind === 'direct';
  const starts = simStarts();
  const start = starts.find((st) => st.id === simStart) || starts[0];
  const link = linkKind === 'onboard' ? { target: 'mqtt' }
    : linkKind === 'sim' ? simLink(start, simLat, simLon)
      : directLink({ mode: directMode, serialTarget, tcpHost, tcpPort, udpPort });
  const status = statusTone(connectionStatus);

  // Use parent's loadDrones to refresh after mutations (add/connect/disconnect/delete)
  const refreshDrones = loadDronesProp || (async () => {});

  useEffect(() => {
    if (!showAddDrone || isDesktopApp) return undefined;
    let cancelled = false;
    apiFetch(`${API_URL}/health`)
      .then((res) => res.json())
      .then((h) => { if (!cancelled) setBridgeUp(h?.services?.python === 'up'); })
      .catch(() => { if (!cancelled) setBridgeUp(false); });
    return () => { cancelled = true; };
  }, [showAddDrone, isDesktopApp]);

  useEffect(() => {
    if (showAddDrone && isDirect && directAvailable && directMode === 'serial') {
      scanPorts();
    }
  }, [showAddDrone, isDirect, directAvailable, directMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const scanPorts = async () => {
    setScanningPorts(true);
    setConnectionStatus('Looking for ports…');
    try {
      const res = await apiFetch(`${API_URL}/ports/scan`);
      const data = await res.json();
      if (data.success && data.ports) {
        const ports = Array.isArray(data.ports) ? data.ports : [];
        setAvailablePorts(ports);

        if (ports.length > 0) {
          const firstPort = ports[0].port;
          setSerialTarget((current) => {
            const currentValue = String(current || '').trim();
            const currentIsDetected = ports.some((port) => port.port === currentValue);
            return currentIsDetected || currentValue ? currentValue : firstPort;
          });
          setConnectionStatus(`Found ${ports.length} port${ports.length > 1 ? 's' : ''}.`);
        } else {
          setConnectionStatus('⚠️ No ports found. Check the cable, or type the port.');
        }
      } else {
        setAvailablePorts([]);
        setConnectionStatus(data.message || 'No ports found');
      }
    } catch (error) {
      setAvailablePorts([]);
      setConnectionStatus('❌ Could not look for ports.');
      logError('CONNECTION', `Port scan failed: ${error.message}`, { error: error.message });
    } finally {
      setScanningPorts(false);
    }
  };

  const addDrone = async () => {
    const connectionString = link.target;

    if (!newDrone.name.trim() || !connectionString) {
      showToast(link.problem || 'Enter a name for the drone', 'info');
      return;
    }

    setConnecting(true);
    setConnectionStatus('Adding drone…');

    try {
      const res = await apiFetch(`${API_URL}/drones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newDrone,
          type: 'ArduPilot', // the only firmware UTMS commands
          connectionString,
          isSimulated: linkKind === 'sim',
        }),
      });

      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to add drone');

      const addedDrone = data.drone;
      const targetDroneId = addedDrone?.id || addedDrone?.droneId || data?.droneId || data?.id;

      // Onboard drones connect themselves: hand over to the setup step instead.
      if (linkKind === 'onboard') {
        if (onDroneSelect && targetDroneId) onDroneSelect(targetDroneId);
        await refreshDrones();
        closeModal();
        setSetupTarget({ id: targetDroneId, name: addedDrone?.name || newDrone.name.trim() });
        return;
      }

      setConnectionStatus('Connecting…');

      const connectRes = await apiFetch(`${API_URL}/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          droneId: targetDroneId,
          port: connectionString,
          baud: addedDrone?.baudRate || newDrone.baudRate || DEFAULT_BAUD_RATE,
        }),
      });

      const connectData = await connectRes.json();
      if (connectData.success) {
        if (onDroneSelect && targetDroneId) onDroneSelect(targetDroneId);
        await refreshDrones();
        showToast('Drone added and connected', 'success');
        closeModal();
      } else {
        if (onDroneSelect && targetDroneId) onDroneSelect(targetDroneId);
        await refreshDrones();
        setConnectionStatus(`⚠️ Added, but the link did not open${connectData.message ? `: ${parseConnectionError(connectData.message)}` : '.'}`);
        showToast(`Drone added but connection failed: ${connectData.message || ''}`, 'warning');
        setConnecting(false);
      }
    } catch (error) {
      setConnectionStatus('❌ ' + parseConnectionError(error.message));
      showToast('Failed: ' + error.message, 'error');
      logError('CONNECTION', `Add drone failed: ${error.message}`, {
        name: newDrone.name,
        connectionString,
        error: error.message,
      });
      setConnecting(false);
    }
  };

  const closeModal = () => {
    setShowAddDrone(false);
    setNewDrone({ name: '', baudRate: DEFAULT_BAUD_RATE, vehicleModel: 'quadcopter' });
    setLinkKind('onboard');
    setDirectMode('serial');
    setSerialTarget('');
    setTcpHost('127.0.0.1');
    setTcpPort(DEFAULT_TCP_PORT);
    setUdpPort(DEFAULT_UDP_PORT);
    setAvailablePorts([]);
    setSimStart('');
    setSimLat('');
    setSimLon('');
    setConnectionStatus('');
    setConnecting(false);
  };

  const pickKind = (kind) => {
    setLinkKind(kind);
    setConnectionStatus('');
    if (kind === 'sim' && !newDrone.name.trim()) {
      setNewDrone({ ...newDrone, name: `Demo Copter ${drones.length + 1}` });
    }
  };

  const deleteDrone = async (droneId) => {
    const confirmed = await showConfirm('Delete this drone?');
    if (!confirmed) return;

    try {
      const res = await apiFetch(`${API_URL}/drones/${droneId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        await refreshDrones();
        // Re-select after state update (avoids setState-during-render)
        if (selectedDroneId === droneId && onDroneSelect) {
          setTimeout(() => onDroneSelect(null), 0);
        }
        showToast('Drone deleted', 'success');
      }
    } catch (error) {
      showToast('Failed to delete drone', 'error');
    }
  };

  const connectDrone = async (droneId) => {
    if (connectingDrones[droneId]) {
      showToast('Connection attempt already in progress', 'info');
      return;
    }
    
    const drone = (drones || []).find(d => d && (d.id === droneId || d.droneId === droneId));
    if (!drone && !droneId) return;

    setConnectingDrones((prev) => ({ ...prev, [droneId]: true }));
    try {
      const res = await apiFetch(`${API_URL}/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          droneId: drone?.id || drone?.droneId || droneId,
          port: drone?.connectionString,
          baud: drone?.baudRate || 57600,
        }),
      });
      const data = await res.json();
      if (data.success) {
        await refreshDrones();
        showToast('Connected', 'success');
      } else {
        showToast(parseConnectionError(data.message), 'error');
      }
    } catch (error) {
      showToast('Connection failed', 'error');
      logError('CONNECTION', `Connect drone failed: ${error.message}`, {
        droneId,
        connectionString: drone?.connectionString,
        error: error.message,
      });
    } finally {
      setConnectingDrones((prev) => ({ ...prev, [droneId]: false }));
    }
  };

  const openEditModal = (drone, e) => {
    e.stopPropagation();
    setEditingDrone(drone);
    setEditDroneData({
      name: drone.name || '',
      connectionString: drone.connectionString || '',
      baudRate: drone.baudRate || DEFAULT_BAUD_RATE,
      vehicleModel: drone.vehicleModel || 'quadcopter',
    });
  };

  const saveEditDrone = async () => {
    if (!editDroneData.name.trim()) {
      showToast('Name cannot be empty', 'info');
      return;
    }
    setEditSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/drones/${editingDrone.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editDroneData),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Drone updated', 'success');
        setEditingDrone(null);
        await refreshDrones();
      } else {
        showToast(data.message || 'Failed to update', 'error');
      }
    } catch (error) {
      showToast('Failed to update drone', 'error');
    } finally {
      setEditSaving(false);
    }
  };

  const disconnectDrone = async (droneId) => {
    try {
      const res = await apiFetch(`${API_URL}/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ droneId }),
      });
      const data = await res.json();
      if (data.success) {
        await refreshDrones();
        showToast('Disconnected', 'success');
      }
    } catch (error) {
      showToast('Failed to disconnect', 'error');
      logError('CONNECTION', `Disconnect failed: ${error.message}`, { droneId, error: error.message });
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'var(--bgPrimary)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: isCollapsed ? '8px 8px' : '12px 16px',
        borderBottom: '1px solid var(--borderSecondary)',
        background: 'var(--bgSecondary)',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}>
        {/* Top row: Toggle button always visible */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          width: '100%'
        }}>
          {!isCollapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
              </svg>
              <h2 style={{
                margin: 0,
                color: 'var(--textPrimary)',
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'uppercase'
              }}>
                Fleet Manager
              </h2>
            </div>
          )}
        </div>

        {/* Drone count - only when expanded */}
        {!isCollapsed && (
          <span style={{
            fontSize: '10px',
            color: 'var(--textSecondary)',
            background: 'var(--bgTertiary)',
            padding: '4px 8px',
            border: '1px solid var(--borderSecondary)',
            borderRadius: '3px',
            alignSelf: 'flex-start'
          }}>
            {drones.length} Drones
          </span>
        )}

        {/* Add Drone Button - only when expanded */}
        {!isCollapsed && (
          <button
            onClick={() => setShowAddDrone(true)}
            style={{
              width: '100%',
              padding: '12px',
              background: 'transparent',
              color: 'var(--accentSecondary)',
              border: '2px dashed color-mix(in srgb, var(--accent) 40%, transparent)',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>add</span>
            Add Drone
          </button>
        )}

        {/* Collapsed: Add as small icon button */}
        {isCollapsed && (
          <button
            onClick={() => setShowAddDrone(true)}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '6px',
              fontSize: '18px',
              background: 'var(--accent)',
              color: '#000',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            title="Add drone"
          >
            +
          </button>
        )}
      </div>

      {/* Drone List */}
      <div className="custom-scrollbar" style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: isCollapsed ? 'center' : 'stretch',
        padding: isCollapsed ? '8px 4px' : '12px',
        gap: isCollapsed ? '8px' : '12px'
      }}>
        {drones.length === 0 ? (
          !isCollapsed && (
            <div style={{ color: 'var(--textMuted)', textAlign: 'center', padding: '40px 16px', fontSize: '12px' }}>
              No drones in fleet
            </div>
          )
        ) : (
          drones.map(drone => (
            isCollapsed ? (
              // Collapsed: Icon only
              <button
                key={drone.id}
                draggable
                onDragStart={(e) => {
                  setDraggedDroneId(drone.id);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', drone.id);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dragOverDroneId !== drone.id) setDragOverDroneId(drone.id);
                }}
                onDragLeave={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget)) return;
                  setDragOverDroneId(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const sourceId = e.dataTransfer.getData('text/plain') || draggedDroneId;
                  const targetId = drone.id;
                  if (sourceId && targetId && sourceId !== targetId) {
                    handleReorder(sourceId, targetId);
                  }
                  setDraggedDroneId(null);
                  setDragOverDroneId(null);
                }}
                onDragEnd={() => {
                  setDraggedDroneId(null);
                  setDragOverDroneId(null);
                }}
                onClick={() => onDroneSelect && onDroneSelect(drone.id)}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '6px',
                  background: selectedDroneId === drone.id ? 'rgba(6, 182, 212, 0.2)' : 'var(--bgSecondary)',
                  border: dragOverDroneId === drone.id
                    ? '2px dashed var(--accent)'
                    : (selectedDroneId === drone.id ? '2px solid var(--accent)' : '1px solid var(--borderSecondary)'),
                  cursor: 'grab',
                  opacity: draggedDroneId === drone.id ? 0.4 : 1,
                  fontSize: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  transition: 'all 0.2s'
                }}
                title={`${drone.name} (${drone.status || 'offline'}) - Drag to rearrange`}
              >
                🚁
                {linkState(drone) !== 'offline' && (
                  <span style={{
                    position: 'absolute',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: linkState(drone) === 'ready' ? 'var(--success)' : 'var(--warning)',
                    bottom: '-2px',
                    right: '-2px',
                    border: '1px solid var(--bgSecondary)'
                  }}></span>
                )}
              </button>
            ) : (
              // Expanded: Full details (Stitch card)
              <div
                key={drone.id}
                draggable
                onDragStart={(e) => {
                  setDraggedDroneId(drone.id);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', drone.id);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dragOverDroneId !== drone.id) setDragOverDroneId(drone.id);
                }}
                onDragLeave={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget)) return;
                  setDragOverDroneId(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const sourceId = e.dataTransfer.getData('text/plain') || draggedDroneId;
                  const targetId = drone.id;
                  if (sourceId && targetId && sourceId !== targetId) {
                    handleReorder(sourceId, targetId);
                  }
                  setDraggedDroneId(null);
                  setDragOverDroneId(null);
                }}
                onDragEnd={() => {
                  setDraggedDroneId(null);
                  setDragOverDroneId(null);
                }}
                onClick={() => onDroneSelect && onDroneSelect(drone.id)}
                className="stitch-fleet-card"
                style={{
                  padding: '16px',
                  background: 'var(--stitch-surface-lowest)',
                  borderRadius: '12px',
                  border: dragOverDroneId === drone.id
                    ? '2px dashed var(--accent)'
                    : (selectedDroneId === drone.id ? '1px solid var(--accent)' : '1px solid color-mix(in srgb, var(--borderSecondary) 30%, transparent)'),
                  cursor: 'grab',
                  opacity: draggedDroneId === drone.id ? 0.4 : 1,
                  transform: dragOverDroneId === drone.id ? 'scale(1.02)' : 'none',
                  transition: 'all 0.15s ease',
                  width: '100%',
                  boxSizing: 'border-box',
                  position: 'relative',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', minWidth: 0 }}>
                    <span
                      className="material-symbols-outlined"
                      title="Drag to rearrange"
                      style={{
                        fontSize: '18px',
                        color: 'var(--textMuted)',
                        cursor: 'grab',
                        marginTop: '2px',
                        userSelect: 'none',
                        opacity: 0.6,
                        flexShrink: 0,
                      }}
                    >
                      drag_indicator
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <h4 style={{ margin: 0, color: 'var(--textPrimary)', fontWeight: 600, fontSize: '16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{drone.name}</h4>
                        {drone.isSimulated && (
                          <span style={{
                            fontSize: '9px',
                            fontWeight: 800,
                            background: 'color-mix(in srgb, var(--accentSecondary) 15%, transparent)',
                            color: 'var(--accentSecondary)',
                            border: '1px solid color-mix(in srgb, var(--accentSecondary) 40%, transparent)',
                            borderRadius: '4px',
                            padding: '1px 5px',
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            flexShrink: 0,
                          }}>
                            SIM
                          </span>
                        )}
                      </div>
                      <p style={{ margin: '4px 0 0', fontFamily: 'var(--font-label)', fontSize: '10px', color: 'var(--textSecondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {drone.type || 'MAVLink'}{drone.battery !== undefined && drone.status === 'connected' ? ` • 🔋 ${Math.round(drone.battery)}%` : ''}
                      </p>
                      <p style={{ margin: '2px 0 0', fontFamily: 'var(--font-label)', fontSize: '11px', color: 'var(--accent)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{isOnboard(drone) ? '4G onboard' : (drone.connectionString || 'N/A')}</p>
                    </div>
                  </div>
                  {linkState(drone) === 'noAutopilot' ? (
                    <span title="The onboard computer is online but the flight controller is not answering" style={{ padding: '4px 8px', background: 'color-mix(in srgb, var(--warning) 10%, transparent)', color: 'var(--warning)', fontSize: '10px', fontWeight: 700, borderRadius: '4px', border: '1px solid color-mix(in srgb, var(--warning) 30%, transparent)', textTransform: 'uppercase', flexShrink: 0 }}>
                      No autopilot
                    </span>
                  ) : drone.status === 'connected' ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px', background: 'color-mix(in srgb, var(--accent) 10%, transparent)', color: 'var(--accentSecondary)', fontSize: '10px', fontWeight: 700, borderRadius: '4px', border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)', textTransform: 'uppercase', flexShrink: 0 }}>
                      <span style={{ position: 'relative', display: 'inline-flex', width: '6px', height: '6px' }}>
                        <span className="stitch-ping" style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--accent)' }} />
                        <span style={{ position: 'relative', width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent)' }} />
                      </span>
                      Online
                    </span>
                  ) : (
                    <span style={{ padding: '4px 8px', background: 'var(--bgTertiary)', color: 'var(--textSecondary)', fontSize: '10px', fontWeight: 700, borderRadius: '4px', textTransform: 'uppercase', flexShrink: 0 }}>Offline</span>
                  )}
                </div>
                <div
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}
                >
                  {isOnboard(drone) ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); setSetupTarget({ id: drone.id, name: drone.name }); }}
                      style={{ padding: '6px 0', background: 'color-mix(in srgb, var(--accent) 10%, transparent)', color: 'var(--accentSecondary)', border: 'none', borderRadius: '8px', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}
                    >
                      SETUP
                    </button>
                  ) : drone.status === 'connected' ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); disconnectDrone(drone.id); }}
                      style={{ padding: '6px 0', background: 'color-mix(in srgb, var(--danger) 10%, transparent)', color: 'var(--danger)', border: 'none', borderRadius: '8px', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}
                    >
                      DISCONNECT
                    </button>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); connectDrone(drone.id); }}
                      disabled={connectingDrones[drone.id]}
                      style={{ padding: '6px 0', background: 'color-mix(in srgb, var(--accent) 10%, transparent)', color: 'var(--accentSecondary)', border: 'none', borderRadius: '8px', fontSize: '10px', fontWeight: 700, cursor: connectingDrones[drone.id] ? 'not-allowed' : 'pointer' }}
                    >
                      {connectingDrones[drone.id] ? '...' : 'CONNECT'}
                    </button>
                  )}
                  <button
                    onClick={(e) => openEditModal(drone, e)}
                    style={{ padding: '6px 0', background: 'var(--bgTertiary)', color: 'var(--textPrimary)', border: 'none', borderRadius: '8px', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    EDIT
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteDrone(drone.id); }}
                    style={{ padding: '6px 0', background: 'var(--bgTertiary)', color: 'var(--textSecondary)', border: 'none', borderRadius: '8px', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    DELETE
                  </button>
                </div>
              </div>
            )
          ))
        )}
      </div>

      {/* Add Drone Modal */}
      {showAddDrone && (
        <div className="adw-overlay" role="dialog" aria-modal="true" aria-labelledby="adw-title">
          <style>{DRONE_DIALOG_CSS}</style>
          <div className="adw">
            <header className="adw-head">
              <h3 id="adw-title">Add drone</h3>
              <p>How does this drone reach UTMS?</p>
            </header>

            <div className="adw-body">
              <div className="adw-kinds" role="radiogroup" aria-label="How the drone reaches UTMS">
                {LINK_KINDS.map((kind) => {
                  const unavailable = kind.id === 'direct' && !directAvailable;
                  return (
                    <button
                      key={kind.id}
                      type="button"
                      role="radio"
                      aria-checked={linkKind === kind.id}
                      className="adw-kind"
                      onClick={() => pickKind(kind.id)}
                      disabled={connecting || unavailable}
                    >
                      <span className="material-symbols-outlined ic" aria-hidden="true">{kind.icon}</span>
                      <span>
                        <b>{kind.title}</b>
                        <small>{unavailable ? kind.unavailable : kind.body}</small>
                      </span>
                      {unavailable ? <span className="adw-tag">Desktop app</span> : kind.tag && <span className="adw-tag">{kind.tag}</span>}
                    </button>
                  );
                })}
              </div>

              <div className="adw-row">
                <div className="adw-field">
                  <label htmlFor="adw-name">Name</label>
                  <input
                    id="adw-name"
                    type="text"
                    value={newDrone.name}
                    onChange={(e) => setNewDrone({ ...newDrone, name: e.target.value })}
                    placeholder="e.g. Hexa 1"
                    disabled={connecting}
                  />
                </div>
                <div className="adw-field">
                  <label htmlFor="adw-frame">Frame</label>
                  <select
                    id="adw-frame"
                    value={newDrone.vehicleModel || 'quadcopter'}
                    onChange={(e) => setNewDrone({ ...newDrone, vehicleModel: e.target.value })}
                    disabled={connecting}
                  >
                    {FRAMES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
              </div>

              {isDirect && directAvailable && (
                <div className="adw-field">
                  <label>Connection</label>
                  <div className="adw-seg" role="group" aria-label="Connection">
                    {DIRECT_MODES.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        aria-pressed={directMode === m.id}
                        onClick={() => { setDirectMode(m.id); setConnectionStatus(''); }}
                        disabled={connecting}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                  <p className="adw-hint">{DIRECT_MODES.find((m) => m.id === directMode).hint}</p>
                </div>
              )}

              {isDirect && directAvailable && directMode === 'serial' && (
                <div className="adw-row wide">
                  <div className="adw-field">
                    <label htmlFor="adw-port">Port</label>
                    <div className="adw-port">
                      <input
                        id="adw-port"
                        className="mono"
                        list="adw-ports"
                        value={serialTarget}
                        onChange={(e) => setSerialTarget(e.target.value)}
                        placeholder="COM6 or /dev/ttyUSB0"
                        disabled={connecting}
                      />
                      <datalist id="adw-ports">
                        {availablePorts.map((port) => (
                          <option key={port.port} value={port.port}>{[port.description, port.manufacturer].filter(Boolean).join(' · ')}</option>
                        ))}
                      </datalist>
                      <button type="button" className="adw-btn ghost sm" onClick={scanPorts} disabled={scanningPorts || connecting}>
                        {scanningPorts ? 'Scanning…' : 'Rescan'}
                      </button>
                    </div>
                  </div>
                  <div className="adw-field">
                    <label htmlFor="adw-baud">Baud rate</label>
                    <select
                      id="adw-baud"
                      value={newDrone.baudRate}
                      onChange={(e) => setNewDrone({ ...newDrone, baudRate: parseInt(e.target.value, 10) })}
                      disabled={connecting}
                    >
                      {BAUD_RATES.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {isDirect && directAvailable && directMode === 'udp' && (
                <div className="adw-row">
                  <div className="adw-field">
                    <label htmlFor="adw-udp">Listen port</label>
                    <input
                      id="adw-udp"
                      className="mono"
                      type="number"
                      min="1"
                      max="65535"
                      value={udpPort}
                      onChange={(e) => setUdpPort(e.target.value === '' ? '' : Number.parseInt(e.target.value, 10))}
                      disabled={connecting}
                    />
                  </div>
                </div>
              )}

              {isDirect && directAvailable && directMode === 'tcp' && (
                <div className="adw-row wide">
                  <div className="adw-field">
                    <label htmlFor="adw-tcp-host">Host</label>
                    <input
                      id="adw-tcp-host"
                      className="mono"
                      type="text"
                      value={tcpHost}
                      onChange={(e) => setTcpHost(e.target.value)}
                      placeholder="127.0.0.1"
                      disabled={connecting}
                    />
                  </div>
                  <div className="adw-field">
                    <label htmlFor="adw-tcp-port">Port</label>
                    <input
                      id="adw-tcp-port"
                      className="mono"
                      type="number"
                      min="1"
                      max="65535"
                      value={tcpPort}
                      onChange={(e) => setTcpPort(e.target.value === '' ? '' : Number.parseInt(e.target.value, 10))}
                      disabled={connecting}
                    />
                  </div>
                </div>
              )}

              {isDirect && directAvailable && link.target && (
                <p className="adw-hint" style={{ margin: '-6px 0 0' }}>Opens <code>{link.target}</code></p>
              )}

              {linkKind === 'sim' && (
                <div className="adw-field">
                  <label htmlFor="adw-start">Start position</label>
                  <select id="adw-start" value={start.id} onChange={(e) => setSimStart(e.target.value)} disabled={connecting}>
                    {starts.map((st) => <option key={st.id} value={st.id}>{st.label}</option>)}
                  </select>
                  {start.id === 'custom' && (
                    <div className="adw-row" style={{ marginTop: 8 }}>
                      <input className="mono" type="number" step="any" value={simLat} onChange={(e) => setSimLat(e.target.value)} placeholder="Latitude, e.g. 21.1458" aria-label="Latitude" disabled={connecting} />
                      <input className="mono" type="number" step="any" value={simLon} onChange={(e) => setSimLon(e.target.value)} placeholder="Longitude, e.g. 79.0882" aria-label="Longitude" disabled={connecting} />
                    </div>
                  )}
                  <p className="adw-hint">
                    Flies missions in software with simulated GPS and battery, and disconnects after 15 minutes idle.
                    {!isAdmin && ' Operators can keep one simulator.'}
                  </p>
                </div>
              )}

              {linkKind === 'onboard' && (
                <div className="adw-note">
                  <p>Next you copy this drone's settings to its onboard computer. After that it comes online by itself whenever it is powered on.</p>
                  <p>Already running UTMS on this drone? Don't add it again: choose Edit on its card and tick 4G onboard computer. Its ID stays the same.</p>
                </div>
              )}
            </div>

            <footer className="adw-foot">
              <span className={`adw-status ${status.tone}`} role="status">
                {status.text || (newDrone.name.trim() ? (isDirect && !directAvailable ? '' : link.problem) : 'Give the drone a name.')}
              </span>
              <button type="button" className="adw-btn ghost" onClick={closeModal} disabled={connecting}>Cancel</button>
              <button
                type="button"
                className="adw-btn primary"
                onClick={addDrone}
                disabled={connecting || !newDrone.name.trim() || !link.target || (isDirect && !directAvailable)}
              >
                {connecting
                  ? (isDirect ? 'Connecting…' : 'Adding…')
                  : (linkKind === 'onboard' ? 'Add drone' : linkKind === 'sim' ? 'Add simulator' : 'Add and connect')}
              </button>
            </footer>
          </div>
        </div>
      )}

      {setupTarget && (
        <OnboardSetup
          drone={drones.find((d) => d.id === setupTarget.id) || setupTarget}
          onClose={() => setSetupTarget(null)}
        />
      )}

      {/* Edit Drone Modal */}
      {editingDrone && (
        <div className="adw-overlay" role="dialog" aria-modal="true" aria-labelledby="adw-edit-title">
          <style>{DRONE_DIALOG_CSS}</style>
          <div className="adw sm">
            <header className="adw-head">
              <h3 id="adw-edit-title">Edit drone</h3>
              <p className="mono">{editingDrone.id}</p>
            </header>

            <div className="adw-body">
              <div className="adw-field">
                <label htmlFor="adw-edit-name">Name</label>
                <input
                  id="adw-edit-name"
                  type="text"
                  value={editDroneData.name}
                  onChange={(e) => setEditDroneData({ ...editDroneData, name: e.target.value })}
                  placeholder="e.g. Hexa 1"
                  disabled={editSaving}
                />
              </div>

              <label className="adw-check">
                <input
                  type="checkbox"
                  checked={isOnboard(editDroneData)}
                  onChange={(e) => setEditDroneData({ ...editDroneData, connectionString: e.target.checked ? 'mqtt' : '' })}
                  disabled={editSaving}
                />
                <span>4G onboard computer. The drone connects to UTMS by itself.</span>
              </label>

              {!isOnboard(editDroneData) && (
                <div className="adw-row wide">
                  <div className="adw-field">
                    <label htmlFor="adw-edit-conn">Connection</label>
                    <input
                      id="adw-edit-conn"
                      className="mono"
                      type="text"
                      value={editDroneData.connectionString}
                      onChange={(e) => setEditDroneData({ ...editDroneData, connectionString: e.target.value })}
                      placeholder="COM6, udpin:0.0.0.0:14550, tcp:127.0.0.1:5760"
                      disabled={editSaving}
                    />
                  </div>
                  <div className="adw-field">
                    <label htmlFor="adw-edit-baud">Baud rate</label>
                    <select
                      id="adw-edit-baud"
                      value={editDroneData.baudRate}
                      onChange={(e) => setEditDroneData({ ...editDroneData, baudRate: parseInt(e.target.value, 10) })}
                      disabled={editSaving}
                    >
                      {withCurrent(BAUD_RATES.map((b) => ({ value: b, label: String(b) })), editDroneData.baudRate).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="adw-field">
                <label htmlFor="adw-edit-frame">Frame</label>
                <select
                  id="adw-edit-frame"
                  value={editDroneData.vehicleModel || 'quadcopter'}
                  onChange={(e) => setEditDroneData({ ...editDroneData, vehicleModel: e.target.value })}
                  disabled={editSaving}
                >
                  {withCurrent(FRAMES, editDroneData.vehicleModel).map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
            </div>

            <footer className="adw-foot">
              <span className="adw-status" />
              <button type="button" className="adw-btn ghost" onClick={() => setEditingDrone(null)} disabled={editSaving}>Cancel</button>
              <button type="button" className="adw-btn primary" onClick={saveEditDrone} disabled={editSaving || !editDroneData.name.trim()}>
                {editSaving ? 'Saving…' : 'Save'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default DroneFleet;
